import { Greeks } from '../calculationService';

import { isBuy, isSell } from '../legService';
import { getTimeToMaturityInYears } from '../helpers/date';

export const keyMetricsCalculations = {
  calculateKeyMetrics
};

/**
 * Calculate key metrics for two legs (OPTION + OPTION || STOCK + OPTION)
 *
 * @param {String} strategy
 * @param {String} netDebit
 * @param {Object} leg1
 * @param {Object} leg2
 * @param {String} interestRate
 * @param {String} dividendYield
 *
 * @return {Object} key metrics data
 */
export function calculateKeyMetrics(strategy, netDebit, leg1, leg2, interestRate, dividendYield) {
  let data = {};

  switch (
    strategy // TODO: how to handle closing strategies
  ) {
    //SingleLeg
    case 'LONG_STOCK':
      data = calcLongStock(leg1 !== null ? leg1 : leg2, netDebit);
      break;
    case 'SHORT_STOCK':
      data = calcShortStock(leg1 !== null ? leg1 : leg2, netDebit);
      break;
    case 'LONG_CALL':
      data = calcLongCall(leg1 !== null ? leg1 : leg2, netDebit);
      break;
    case 'SHORT_CALL':
      data = calcShortCall(leg1 !== null ? leg1 : leg2, netDebit);
      break;
    case 'LONG_PUT':
      data = calcLongPut(leg1 !== null ? leg1 : leg2, netDebit);
      break;
    case 'SHORT_PUT':
      data = calcShortPut(leg1 !== null ? leg1 : leg2, netDebit);
      break;

    //Stock + Option
    case 'COVERED_CALL':
      data = calcCoveredCall(leg1, leg2, netDebit);
      break;
    case 'PROTECTED_PUT':
      data = calcProtectedPut(leg1, leg2, netDebit);
      break;

    // Option + Option
    case 'LONG_CALL_CALENDAR':
      data = calcLongCallCalendar(leg1, leg2, netDebit, interestRate, dividendYield);
      break;
    case 'LONG_PUT_CALENDAR':
      data = calcLongPutCalendar(leg1, leg2, netDebit, interestRate, dividendYield);
      break;
    case 'SHORT_CALL_CALENDAR':
      data = calcShortCallCalendar(leg1, leg2, netDebit, interestRate, dividendYield);
      break;
    case 'SHORT_PUT_CALENDAR':
      data = calcShortPutCalendar(leg1, leg2, netDebit, interestRate, dividendYield);
      break;

    case 'DIAGONAL_BULL_CALL_SPREAD':
      data = calcDiagonalBullCallSpread(leg1, leg2, netDebit, interestRate, dividendYield);
      break;
    case 'DIAGONAL_BEAR_CALL_SPREAD':
      data = calcDiagonalBearCallSpread(leg1, leg2, netDebit, interestRate, dividendYield);
      break;
    case 'DIAGONAL_BULL_PUT_SPREAD':
      data = calcDiagonalBullPutSpread(leg1, leg2, netDebit, interestRate, dividendYield);
      break;
    case 'DIAGONAL_BEAR_PUT_SPREAD':
      data = calcDiagonalBearPutSpread(leg1, leg2, netDebit, interestRate, dividendYield);
      break;

    case 'LONG_STRADDLE':
      data = calcLongStraddle(leg1, leg2, netDebit);
      break;
    case 'SHORT_STRADDLE':
      data = calcShortStraddle(leg1, leg2, netDebit);
      break;
    case 'LONG_SYNTHETIC_STOCK':
      data = calcLongSyntheticStock(leg1, leg2, netDebit);
      break;
    case 'SHORT_SYNTHETIC_STOCK':
      data = calcShortSyntheticStock(leg1, leg2, netDebit);
      break;
    case 'LONG_STRANGLE':
      data = calcLongStrangle(leg1, leg2, netDebit);
      break;
    case 'SHORT_STRANGLE':
      data = calcShortStrangle(leg1, leg2, netDebit);
      break;
    case 'BEAR_CALL_SPREAD':
      data = calcBearCallSpread(leg1, leg2, netDebit);
      break;
    case 'BULL_CALL_SPREAD':
      data = calcBullCallSpread(leg1, leg2, netDebit);
      break;
    case 'BEAR_PUT_SPREAD':
      data = calcBearPutSpread(leg1, leg2, netDebit);
      break;
    case 'BULL_PUT_SPREAD':
      data = calcBullPutSpread(leg1, leg2, netDebit);
      break;
    case 'CALL_BACK_SPREAD':
      data = calcCallBackSpread(leg1, leg2, netDebit);
      break;
    case 'CALL_RATIO_SPREAD':
      data = calcCallRatioSpread(leg1, leg2, netDebit);
      break;
    case 'PUT_BACK_SPREAD':
      data = calcPutBackSpread(leg1, leg2, netDebit);
      break;
    case 'PUT_RATIO_SPREAD':
      data = calcPutRatioSpread(leg1, leg2, netDebit);
      break;
    default:
      data = { maxProfit: '-', maxLoss: '-', breakEvenLow: '-', breakEvenHigh: '-', maxProfitPrice: '-' };
  }
  return data;
}

function getOptionQuantityForKeyMetrics(leg) {
  return leg.quantity * leg.option['xopt.LOT_SIZE_A'];
  //	return leg.getQuantity();
}

//SingleLeg
function calcLongStock(leg1, netDebit) {
  const data = {};
  const quantity = leg1.quantity;
  data.maxProfit = Infinity;
  data.maxLoss = netDebit * quantity;
  data.breakEvenLow = netDebit;
  data.breakEvenHigh = NaN;
  data.maxProfitPrice = Infinity;
  return data;
}

function calcShortStock(leg1, netDebit) {
  const data = {};
  const quantity = leg1.quantity;
  data.maxProfit = -netDebit * quantity;
  data.maxLoss = Infinity;
  data.breakEvenLow = NaN;
  data.breakEvenHigh = -netDebit;
  data.maxProfitPrice = 0;
  return data;
}

function calcLongCall(leg1, netDebit) {
  const data = {};
  const quantity = getOptionQuantityForKeyMetrics(leg1);
  const strikePrice = parseFloat(leg1.option['xopt._STRIKE']);
  data.maxProfit = Infinity;
  data.maxLoss = netDebit * quantity;
  data.breakEvenLow = strikePrice + netDebit;
  data.breakEvenHigh = NaN;
  data.maxProfitPrice = Infinity;
  return data;
}

// netDebit / price for short call is assumed to be negative
function calcShortCall(leg1, netDebit) {
  const data = {};
  const quantity = getOptionQuantityForKeyMetrics(leg1);
  const strikePrice = parseFloat(leg1.option['xopt._STRIKE']);
  data.maxProfit = -netDebit * quantity;
  data.maxLoss = Infinity;
  data.breakEvenLow = NaN;
  data.breakEvenHigh = strikePrice - netDebit;
  data.maxProfitPrice = 0;
  return data;
}

function calcLongPut(leg1, netDebit) {
  const data = {};
  const quantity = getOptionQuantityForKeyMetrics(leg1);
  const strikePrice = parseFloat(leg1.option['xopt._STRIKE']);
  data.maxProfit = (strikePrice - netDebit) * quantity;
  data.maxLoss = netDebit * quantity;
  data.breakEvenLow = NaN;
  data.breakEvenHigh = strikePrice - netDebit;
  data.maxProfitPrice = 0;
  return data;
}

// netDebit / price for short put is assumed to be negative
function calcShortPut(leg1, netDebit) {
  const data = {};
  const quantity = getOptionQuantityForKeyMetrics(leg1);
  const strikePrice = parseFloat(leg1.option['xopt._STRIKE']);
  data.maxProfit = -netDebit * quantity;
  data.maxLoss = (strikePrice + netDebit) * quantity;
  data.breakEvenLow = NaN;
  data.breakEvenHigh = strikePrice + netDebit;
  data.maxProfitPrice = Infinity;
  return data;
}

// -------------- Stock + Option --------------

// Covered Call
function calcCoveredCall(leg1, leg2, netDebit) {
  const data = {};
  const strikePrice = parseFloat(leg2.option['xopt._STRIKE']);

  // TODO: fails when leg2 is a STOCK -> where to fix?! here?before?
  data.maxProfit = (strikePrice - netDebit) * leg1.quantity;
  data.maxLoss = netDebit * leg1.quantity;
  data.breakEvenLow = netDebit;
  data.breakEvenHigh = NaN;
  data.maxProfitPrice = strikePrice;
  return data;
}

// Protected Put
function calcProtectedPut(leg1, leg2, netDebit) {
  const data = {};
  const strikePrice = parseFloat(leg2.option['xopt._STRIKE']);

  data.maxProfit = Infinity;
  data.maxLoss = (netDebit - strikePrice) * leg1.quantity;
  data.breakEvenLow = netDebit;
  data.breakEvenHigh = NaN;
  data.maxProfitPrice = Infinity;
  return data;
}

// -------------- Option + Option --------------

// Long Calendar Call
function calcLongCallCalendar(leg1, leg2, netDebit, interestRate, underlyingDividendYield) {
  const soldLeg = isSell(leg1) ? leg1 : leg2;
  const boughtLeg = isBuy(leg1) ? leg1 : leg2;

  const data = {};
  data.maxProfit = calcModelValue(boughtLeg, soldLeg, netDebit, interestRate, underlyingDividendYield);
  data.maxLoss = netDebit * getOptionQuantityForKeyMetrics(boughtLeg);
  data.breakEvenLow = calendarSpreadBreakEven(
    soldLeg,
    boughtLeg,
    netDebit,
    interestRate,
    underlyingDividendYield,
    true
  );
  data.breakEvenHigh = calendarSpreadBreakEven(
    soldLeg,
    boughtLeg,
    netDebit,
    interestRate,
    underlyingDividendYield,
    false
  );
  data.maxProfitPrice = boughtLeg.option['xopt._STRIKE'];
  return data;
}

// Long Put Calendar
function calcLongPutCalendar(leg1, leg2, netDebit, interestRate, underlyingDividendYield) {
  const data = {};
  data.maxProfit = calcModelValue(leg1, leg2, netDebit, interestRate, underlyingDividendYield);
  data.maxLoss = netDebit * getOptionQuantityForKeyMetrics(leg2);
  data.breakEvenLow = calendarSpreadBreakEven(leg1, leg2, netDebit, interestRate, underlyingDividendYield, true);
  data.breakEvenHigh = calendarSpreadBreakEven(leg1, leg2, netDebit, interestRate, underlyingDividendYield, false);
  data.maxProfitPrice = leg2.option['xopt._STRIKE'];
  return data;
}

// Short Call Calendar
function calcShortCallCalendar(leg1, leg2, netDebit, interestRate, underlyingDividendYield) {
  const data = {};
  data.maxProfit = -netDebit * getOptionQuantityForKeyMetrics(leg1);
  data.maxLoss = calcModelValue(leg1, leg2, -netDebit, interestRate, underlyingDividendYield);
  data.breakEvenLow = shortCalendarSpreadBreakEven(leg1, leg2, netDebit, interestRate, underlyingDividendYield, true);
  data.breakEvenHigh = shortCalendarSpreadBreakEven(leg1, leg2, netDebit, interestRate, underlyingDividendYield, false);
  data.maxProfitPrice = calcMaxUnderlyingPriceforZeroValue(
    leg1,
    leg2,
    underlyingDividendYield,
    interestRate,
    leg2.option['q._VOLA_ASK'] / 100
  ); // .option['q._VOLA_ASK'] or .option._VOLA_BID
  return data;
}

// Short Put Calendar
function calcShortPutCalendar(leg1, leg2, netDebit, interestRate, underlyingDividendYield) {
  const data = {};
  data.maxProfit = -netDebit * getOptionQuantityForKeyMetrics(leg1);
  data.maxLoss = calcModelValue(leg1, leg2, -netDebit, interestRate, underlyingDividendYield);
  data.breakEvenLow = shortCalendarSpreadBreakEven(leg1, leg2, netDebit, interestRate, underlyingDividendYield, true);
  data.breakEvenHigh = shortCalendarSpreadBreakEven(leg1, leg2, netDebit, interestRate, underlyingDividendYield, false);
  data.maxProfitPrice = calcMaxUnderlyingPriceforZeroValue(
    leg1,
    leg2,
    underlyingDividendYield,
    interestRate,
    leg2.option['q._VOLA_ASK'] / 100
  );
  return data;
}

// Diagonal Bull Call Spread
function calcDiagonalBullCallSpread(leg1, leg2, netDebit, interestRate, underlyingDividendYield) {
  const soldLeg = isSell(leg1) ? leg1 : leg2;

  const data = {};
  data.maxProfit = calcModelValue(leg1, leg2, netDebit, interestRate, underlyingDividendYield);
  data.maxLoss = netDebit * getOptionQuantityForKeyMetrics(leg2);
  data.breakEvenLow = calendarSpreadBreakEven(leg1, leg2, netDebit, interestRate, underlyingDividendYield, true); //TODO?
  data.breakEvenHigh = calendarSpreadBreakEven(leg1, leg2, netDebit, interestRate, underlyingDividendYield, false); //TODO?
  data.maxProfitPrice = soldLeg.option['xopt._STRIKE'];
  return data;
}

// Diagonal Bear Call Spread
function calcDiagonalBearCallSpread(leg1, leg2, netDebit, interestRate, underlyingDividendYield) {
  const soldLeg = isSell(leg1) ? leg1 : leg2;
  const boughtLeg = isBuy(leg1) ? leg1 : leg2;

  const data = {};
  data.maxProfit = calcModelValue(leg1, leg2, netDebit, interestRate, underlyingDividendYield);
  data.maxLoss =
    (parseFloat(boughtLeg.option['xopt._STRIKE']) - parseFloat(soldLeg.option['xopt._STRIKE']) + netDebit) *
    getOptionQuantityForKeyMetrics(soldLeg);
  data.breakEvenLow = calendarSpreadBreakEven(leg1, leg2, netDebit, interestRate, underlyingDividendYield, true); //TODO?
  data.breakEvenHigh = calendarSpreadBreakEven(leg1, leg2, netDebit, interestRate, underlyingDividendYield, false); //TODO?
  data.maxProfitPrice = soldLeg.option['xopt._STRIKE'];
  return data;
}

// Diagonal Bull Put Spread
function calcDiagonalBullPutSpread(leg1, leg2, netDebit, interestRate, underlyingDividendYield) {
  const soldLeg = isSell(leg1) ? leg1 : leg2;
  const boughtLeg = isBuy(leg1) ? leg1 : leg2;

  const data = {};
  data.maxProfit = calcModelValue(leg1, leg2, netDebit, interestRate, underlyingDividendYield);
  data.maxLoss =
    (parseFloat(soldLeg.option['xopt._STRIKE']) - parseFloat(boughtLeg.option['xopt._STRIKE']) + netDebit) *
    getOptionQuantityForKeyMetrics(soldLeg);
  data.breakEvenLow = calendarSpreadBreakEven(leg1, leg2, netDebit, interestRate, underlyingDividendYield, true); //TODO?
  data.breakEvenHigh = calendarSpreadBreakEven(leg1, leg2, netDebit, interestRate, underlyingDividendYield, false); //TODO?
  data.maxProfitPrice = soldLeg.option['xopt._STRIKE'];
  return data;
}

// Diagonal Bear Put Spread
function calcDiagonalBearPutSpread(leg1, leg2, netDebit, interestRate, underlyingDividendYield) {
  const soldLeg = isSell(leg1) ? leg1 : leg2;
  const data = {};
  data.maxProfit = calcModelValue(leg1, leg2, netDebit, interestRate, underlyingDividendYield);
  data.maxLoss = netDebit * getOptionQuantityForKeyMetrics(leg2);
  data.breakEvenLow = calendarSpreadBreakEven(leg1, leg2, netDebit, interestRate, underlyingDividendYield, true); //TODO?
  data.breakEvenHigh = calendarSpreadBreakEven(leg1, leg2, netDebit, interestRate, underlyingDividendYield, false); //TODO?
  data.maxProfitPrice = soldLeg.option['xopt._STRIKE'];
  return data;
}

// Long Straddle
function calcLongStraddle(leg1, leg2, netDebit) {
  const putLeg = leg1.option['xopt._TYPE_CP'] === 'P' ? leg1 : leg2;
  const callLeg = leg1.option['xopt._TYPE_CP'] === 'C' ? leg1 : leg2;

  const data = {};
  data.maxProfit = Infinity;
  data.maxLoss = netDebit * getOptionQuantityForKeyMetrics(leg1);
  data.breakEvenLow = parseFloat(putLeg.option['xopt._STRIKE']) - netDebit;
  data.breakEvenHigh = parseFloat(callLeg.option['xopt._STRIKE']) + netDebit;
  data.maxProfitPrice = Infinity;
  return data;
}

// Short Straddle
function calcShortStraddle(leg1, leg2, netDebit) {
  const putLeg = leg1.option['xopt._TYPE_CP'] === 'P' ? leg1 : leg2;
  const callLeg = leg1.option['xopt._TYPE_CP'] === 'C' ? leg1 : leg2;

  const data = {};
  data.maxProfit = -netDebit * getOptionQuantityForKeyMetrics(leg1);
  data.maxLoss = Infinity;
  data.breakEvenLow = parseFloat(putLeg.option['xopt._STRIKE']) + netDebit;
  data.breakEvenHigh = parseFloat(callLeg.option['xopt._STRIKE']) - netDebit;
  data.maxProfitPrice = callLeg.option['xopt._STRIKE'];
  return data;
}

// Long Synthetic Stock
function calcLongSyntheticStock(leg1, leg2, netDebit) {
  const data = {};
  data.maxProfit = Infinity;
  data.maxLoss = (parseFloat(leg1.option['xopt._STRIKE']) + netDebit) * getOptionQuantityForKeyMetrics(leg1);
  data.breakEvenLow = parseFloat(leg1.option['xopt._STRIKE']) + netDebit;
  data.breakEvenHigh = NaN;
  data.maxProfitPrice = Infinity;
  return data;
}

// Short Synthetic Stock
function calcShortSyntheticStock(leg1, leg2, netDebit) {
  const data = {};
  data.maxProfit = (parseFloat(leg1.option['xopt._STRIKE']) - netDebit) * getOptionQuantityForKeyMetrics(leg1);
  data.maxLoss = Infinity;
  data.breakEvenLow = parseFloat(leg1.option['xopt._STRIKE']) - netDebit;
  data.breakEvenHigh = NaN;
  data.maxProfitPrice = 0;
  return data;
}

// Long Strangle
function calcLongStrangle(leg1, leg2, netDebit) {
  const putLeg = leg1.option['xopt._TYPE_CP'] === 'P' ? leg1 : leg2;
  const callLeg = leg1.option['xopt._TYPE_CP'] === 'C' ? leg1 : leg2;

  let optionPricesBtwStrikes = 0;
  const putCallDist = parseFloat(putLeg.option['xopt._STRIKE']) - parseFloat(callLeg.option['xopt._STRIKE']);
  if (putCallDist > 0) {
    optionPricesBtwStrikes = putCallDist * getOptionQuantityForKeyMetrics(leg1);
  }

  const data = {};
  data.maxProfit = Infinity;
  data.maxLoss = netDebit * getOptionQuantityForKeyMetrics(callLeg) - optionPricesBtwStrikes;
  data.breakEvenLow = parseFloat(putLeg.option['xopt._STRIKE']) - netDebit;
  data.breakEvenHigh = parseFloat(callLeg.option['xopt._STRIKE']) + netDebit;
  data.maxProfitPrice = Infinity;
  return data;
}

// Short Strangle
function calcShortStrangle(leg1, leg2, netDebit) {
  const putLeg = leg1.option['xopt._TYPE_CP'] === 'P' ? leg1 : leg2;
  const callLeg = leg1.option['xopt._TYPE_CP'] === 'C' ? leg1 : leg2;

  let optionPricesBtwStrikes = 0;
  const putCallDist = parseFloat(putLeg.option['xopt._STRIKE']) - parseFloat(callLeg.option['xopt._STRIKE']);
  if (putCallDist > 0) {
    optionPricesBtwStrikes = putCallDist * getOptionQuantityForKeyMetrics(leg1);
  }

  const data = {};
  data.maxProfit = -netDebit * getOptionQuantityForKeyMetrics(leg1) - optionPricesBtwStrikes;
  data.maxLoss = Infinity;
  data.breakEvenLow = putLeg.option['xopt._STRIKE'] + netDebit;
  data.breakEvenHigh = callLeg.option['xopt._STRIKE'] - netDebit;
  data.maxProfitPrice = (parseFloat(putLeg.option['xopt._STRIKE']) + parseFloat(callLeg.option['xopt._STRIKE'])) / 2; //Any Price Between the Strike Prices, so take half way
  return data;
}

// Bear Call Spread
function calcBearCallSpread(leg1, leg2, netDebit) {
  const soldLeg = isSell(leg1) ? leg1 : leg2;
  const boughtLeg = isBuy(leg1) ? leg1 : leg2;
  const quantity = getOptionQuantityForKeyMetrics(leg1);

  const data = {};
  data.maxProfit = -netDebit * quantity;
  data.maxLoss =
    (parseFloat(boughtLeg.option['xopt._STRIKE']) - parseFloat(soldLeg.option['xopt._STRIKE']) + netDebit) * quantity;
  data.breakEvenLow = parseFloat(soldLeg.option['xopt._STRIKE']) - netDebit;
  data.breakEvenHigh = NaN;
  data.maxProfitPrice = soldLeg.option['xopt._STRIKE'];
  return data;
}

// Bull Call Spread
function calcBullCallSpread(leg1, leg2, netDebit) {
  const soldLeg = isSell(leg1) ? leg1 : leg2;
  const boughtLeg = isBuy(leg1) ? leg1 : leg2;
  const quantity = getOptionQuantityForKeyMetrics(leg1);

  const data = {};
  data.maxProfit =
    (parseFloat(soldLeg.option['xopt._STRIKE']) - parseFloat(boughtLeg.option['xopt._STRIKE']) - netDebit) * quantity;
  data.maxLoss = netDebit * quantity;
  data.breakEvenLow = parseFloat(boughtLeg.option['xopt._STRIKE']) + netDebit;
  data.breakEvenHigh = NaN;
  data.maxProfitPrice = soldLeg.option['xopt._STRIKE'];
  return data;
}

// Bear But Spread
function calcBearPutSpread(leg1, leg2, netDebit) {
  const soldLeg = isSell(leg1) ? leg1 : leg2;
  const boughtLeg = isBuy(leg1) ? leg1 : leg2;
  const quantity = getOptionQuantityForKeyMetrics(leg1);

  const data = {};
  data.maxProfit =
    (parseFloat(boughtLeg.option['xopt._STRIKE']) - parseFloat(soldLeg.option['xopt._STRIKE']) - netDebit) * quantity;
  data.maxLoss = netDebit * quantity;
  data.breakEvenLow = parseFloat(boughtLeg.option['xopt._STRIKE']) - netDebit;
  data.breakEvenHigh = NaN;
  data.maxProfitPrice = soldLeg.option['xopt._STRIKE'];
  return data;
}

// Bull But Spread
function calcBullPutSpread(leg1, leg2, netDebit) {
  const soldLeg = isSell(leg1) ? leg1 : leg2;
  const boughtLeg = isBuy(leg1) ? leg1 : leg2;
  const quantity = getOptionQuantityForKeyMetrics(leg1);

  const data = {};
  data.maxProfit = -netDebit * quantity;
  data.maxLoss =
    (parseFloat(soldLeg.option['xopt._STRIKE']) - parseFloat(boughtLeg.option['xopt._STRIKE']) + netDebit) * quantity;
  data.breakEvenLow = parseFloat(soldLeg.option['xopt._STRIKE']) + netDebit;
  data.breakEvenHigh = NaN;
  data.maxProfitPrice = soldLeg.option['xopt._STRIKE'];
  return data;
}

// Call Back Spread
function calcCallBackSpread(leg1, leg2, netDebit) {
  const soldLeg = isSell(leg1) ? leg1 : leg2;
  const boughtLeg = isBuy(leg1) ? leg1 : leg2;
  const soldStrike = parseFloat(soldLeg.option['xopt._STRIKE']);
  const boughtStrike = parseFloat(boughtLeg.option['xopt._STRIKE']);
  const soldQuantity = getOptionQuantityForKeyMetrics(soldLeg);
  //const boughtQuantity = getOptionQuantityForKeyMetrics(boughtLeg);
  const amountPaid = netDebit * soldQuantity; // see https://jira.financial.com/browse/TDWATP-1671

  const data = {};
  data.maxProfit = Infinity;
  data.maxLoss = (boughtStrike - soldStrike) * soldQuantity + amountPaid;
  data.breakEvenLow = netDebit > 0 ? NaN : soldStrike - netDebit;
  data.breakEvenHigh = calcRatioCallBreakEven(
    boughtStrike,
    boughtLeg.quantity,
    soldStrike,
    soldLeg.quantity,
    -netDebit
  );
  data.maxProfitPrice = Infinity;
  return data;
}

// Call Ratios Spread
function calcCallRatioSpread(leg1, leg2, netDebit) {
  const soldLeg = isSell(leg1) ? leg1 : leg2;
  const boughtLeg = isBuy(leg1) ? leg1 : leg2;
  const soldStrike = parseFloat(soldLeg.option['xopt._STRIKE']);
  const boughtStrike = parseFloat(boughtLeg.option['xopt._STRIKE']);
  //const soldQuantity = getOptionQuantityForKeyMetrics(soldLeg);
  const boughtQuantity = getOptionQuantityForKeyMetrics(boughtLeg);
  const amountPaid = netDebit * boughtQuantity;

  const data = {};
  data.maxProfit = (soldStrike - boughtStrike) * boughtQuantity - amountPaid;
  data.maxLoss = Infinity;
  data.breakEvenLow = netDebit < 0 ? NaN : boughtStrike + netDebit;
  data.breakEvenHigh = calcRatioCallBreakEven(soldStrike, soldLeg.quantity, boughtStrike, boughtLeg.quantity, netDebit);
  data.maxProfitPrice = soldStrike;
  return data;
}

// Put Back Spread
function calcPutBackSpread(leg1, leg2, netDebit) {
  const soldLeg = isSell(leg1) ? leg1 : leg2;
  const boughtLeg = isBuy(leg1) ? leg1 : leg2;
  const soldStrike = parseFloat(soldLeg.option['xopt._STRIKE']);
  const boughtStrike = parseFloat(boughtLeg.option['xopt._STRIKE']);
  const soldQuantity = getOptionQuantityForKeyMetrics(soldLeg);
  const boughtQuantity = getOptionQuantityForKeyMetrics(boughtLeg);
  const amountPaid = netDebit * soldQuantity;

  const data = {};
  data.maxProfit = boughtStrike * boughtQuantity - soldStrike * soldQuantity - amountPaid;
  data.maxLoss = (soldStrike - boughtStrike) * soldQuantity + amountPaid;
  data.breakEvenLow = calcRatioCallBreakEven(boughtStrike, boughtLeg.quantity, soldStrike, soldLeg.quantity, netDebit);
  data.breakEvenHigh = netDebit > 0 ? NaN : soldStrike + netDebit;
  data.maxProfitPrice = 0;
  return data;
}

// Put Ratio Spread
function calcPutRatioSpread(leg1, leg2, netDebit) {
  const soldLeg = isSell(leg1) ? leg1 : leg2;
  const boughtLeg = isBuy(leg1) ? leg1 : leg2;

  const soldStrike = parseFloat(soldLeg.option['xopt._STRIKE']);
  const boughtStrike = parseFloat(boughtLeg.option['xopt._STRIKE']);
  const soldQuantity = getOptionQuantityForKeyMetrics(soldLeg);
  const boughtQuantity = getOptionQuantityForKeyMetrics(boughtLeg);
  const amountPaid = netDebit * boughtQuantity;

  const data = {};
  data.maxProfit = (boughtStrike - soldStrike) * boughtQuantity - amountPaid;
  data.maxLoss = soldStrike * soldQuantity - boughtStrike * boughtQuantity + netDebit * boughtQuantity;
  data.breakEvenLow = calcRatioCallBreakEven(boughtStrike, boughtLeg.quantity, soldStrike, soldLeg.quantity, netDebit);
  data.breakEvenHigh = netDebit < 0 ? NaN : boughtStrike - netDebit;
  data.maxProfitPrice = soldStrike;
  return data;
}

// calculate the calendar spread value at strike price of the front-month option (i.e. at S = X1) at the expiration of the front-month option, which gives the maximum profit of this calendar spread strategy.
// The front-month call has a value = 0 at S = X and Time = 0.
// TODO: should be named calcCalendarSpreadAtStrike()
function calcModelValue(boughtLeg, soldLeg, netDebit, interestRate, underlyingDividendYield) {
  let nearExpOption; // front-month option
  let farExpOption; // back-month option

  const maturity1 = new Date(Date.parse(boughtLeg.option['xopt._MATURITY_ISO'])).getTime();
  const maturity2 = new Date(Date.parse(soldLeg.option['xopt._MATURITY_ISO'])).getTime();
  //let frontMonthExpiryDate;
  //let backMonthExpiryDate;

  if (maturity1 < maturity2) {
    nearExpOption = boughtLeg;
    farExpOption = soldLeg;
    //rontMonthExpiryDate = maturity1;
    //backMonthExpiryDate = maturity2;
  } else {
    nearExpOption = soldLeg;
    farExpOption = boughtLeg;
    //frontMonthExpiryDate = maturity2;
    //backMonthExpiryDate = maturity1;
  }

  // option price at expiry of the front-month option for S=X
  // (for calendar spreads with different expiration, it is the expiration date of the front month option. We have to price the back-month option with their remaining time to maturity
  const typeFlag = farExpOption.option['xopt._TYPE_CP'];
  const S = parseFloat(nearExpOption.option['xopt._STRIKE']); //
  const X = parseFloat(farExpOption.option['xopt._STRIKE']);
  const Time =
    getTimeToMaturityInYears(farExpOption.option['xopt._MATURITY_ISO']) -
    getTimeToMaturityInYears(nearExpOption.option['xopt._MATURITY_ISO']);
  // interest rate r
  const r = interestRate; // for CAD: CADISC=/PRIMACT_1 = 1.25% //
  // dividend yield r_d
  const r_d = underlyingDividendYield;
  const sigma = parseFloat(farExpOption.option['q._VOLA_ASK']) / 100; // 0.2916
  const optionPrice = Greeks.GBSOption(typeFlag, S, X, Time, r, r - r_d, sigma);

  const totalQuantity = nearExpOption.quantity * nearExpOption.option['xopt.LOT_SIZE_A'];
  const pricePerShare = optionPrice - netDebit;

  return pricePerShare * totalQuantity;
}

function calcRatioCallBreakEven(strikeBought, quantityBought, strikeSold, quantitySold, netCredit) {
  const val = (quantityBought * strikeBought - quantitySold * strikeSold - netCredit) / (quantityBought - quantitySold);
  return val;
}

/**
 * CalendarSpread calculation for call and put (front month option short, back month option long)
 *
 * @param type: "C" or "P"
 * @param S underlying price
 * @param X strike price
 * @param Time1 time to expiration of front-month option
 * @param Time2 time to expiration of back-month option
 * @param r risk-free rate of return
 * @param r_d underlying dividend yield
 * @param sigma1
 * @param sigma2
 */
function CalendarSpread(type, S, X, Time1, Time2, r, r_d, sigma1, sigma2) {
  let typeFlag;
  let fak;
  const ratio = 1;
  let price1;
  let price2;
  const b = r - r_d;

  if (type === 'C') {
    typeFlag = 'C';
    fak = 1;
  } else {
    typeFlag = 'P';
    fak = -1;
  }

  if (Time1 <= 0) {
    price1 = Math.max(fak * (S - X), 0) * ratio;
  } else {
    //option1 = Greeks.GBSOption(typeFlag, S, X, Time1, r, b, sigma1);
    price1 = Math.max(price1, 0);
  }

  if (Time2 === 0) {
    price2 = Math.max(fak * (S - X), 0) * ratio;
  } else {
    price2 = Greeks.GBSOption(typeFlag, S, X, Time2, r, b, sigma2);
    price2 = Math.max(price2, 0);
  }

  const price = price2 - price1; // front month option short, back month option long

  return price;
}

/**
 * Short CalendarSpread calculation for call and put(front month option long, back month option short with same strike price)
 *
 * @param type: "C" or "P"
 * @param S underlying price
 * @param X strike price
 * @param Time1 time to expiration of front-month option
 * @param Time2 time to expiration of back-month option
 * @param r risk-free rate of return
 * @param r_d underlying dividend yield
 * @param sigma1
 * @param sigma2
 */
function ShortCalendarSpread(type, S, X, Time1, Time2, r, r_d, sigma1, sigma2) {
  let typeFlag;
  let fak;
  const ratio = 1;
  let price1;
  let price2;
  const b = r - r_d;

  if (type === 'C') {
    typeFlag = 'C';
    fak = 1;
  } else {
    typeFlag = 'P';
    fak = -1;
  }

  if (Time1 <= 0) {
    price1 = Math.max(fak * (S - X), 0) * ratio;
  } else {
    //option1 = Greeks.GBSOption(typeFlag, S, X, Time1, r, b, sigma1);
    price1 = Math.max(price1, 0);
  }

  if (Time2 === 0) {
    price2 = Math.max(fak * (S - X), 0) * ratio;
  } else {
    price2 = Greeks.GBSOption(typeFlag, S, X, Time2, r, b, sigma2);
    price2 = Math.max(price2, 0);
  }

  const price = price1 - price2; // front month option long, back month option short

  return price;
}

/**
 * Diagonal Spread calculation for call and put (front month option short, back month option long)
 *
 * @param type: "C" or "P"
 * @param S  underlying price
 * @param X1 strike price of front-month option
 * @param X2 strike price of back-month option
 * @param Time1 time to expiration of front-month option
 * @param Time2 time to expiration of back-month option
 * @param r risk-free rate of return
 * @param r_d underlying dividend yield
 * @param sigma1
 * @param sigma2
 */
function DiagonalSpread(type, S, X1, X2, Time1, Time2, r, r_d, sigma1, sigma2) {
  let typeFlag;
  let fak;
  const ratio = 1;
  let price1;
  let price2;
  const b = r - r_d;

  if (type === 'C') {
    typeFlag = 'C';
    fak = 1;
  } else {
    typeFlag = 'P';
    fak = -1;
  }

  if (Time1 <= 0) {
    price1 = Math.max(fak * (S - X1), 0) * ratio;
  } else {
    //option1 = Greeks.GBSOption(typeFlag, S, X1, Time1, r, b, sigma1);
    price1 = Math.max(price1, 0);
  }

  if (Time2 === 0) {
    price2 = Math.max(fak * (S - X2), 0) * ratio;
  } else {
    price2 = Greeks.GBSOption(typeFlag, S, X2, Time2, r, b, sigma2);
    price2 = Math.max(price2, 0);
  }

  const price = price2 - price1; // front month option short, back month option long

  return price;
}

/*
function CallCalendarSpread(S, X, Time1, Time2, r, r_d, sigma1, sigma2) {
  return CalendarSpread('C', S, X, Time1, Time2, r, r_d, sigma1, sigma2);
}
*/

function PutCalendarSpread(S, X, Time1, Time2, r, r_d, sigma1, sigma2) {
  return CalendarSpread('P', S, X, Time1, Time2, r, r_d, sigma1, sigma2);
}

function ShortCallCalendarSpread(S, X, Time1, Time2, r, r_d, sigma1, sigma2) {
  return ShortCalendarSpread('C', S, X, Time1, Time2, r, r_d, sigma1, sigma2);
}

function ShortPutCalendarSpread(S, X, Time1, Time2, r, r_d, sigma1, sigma2) {
  return ShortCalendarSpread('P', S, X, Time1, Time2, r, r_d, sigma1, sigma2);
}

function DiagonalCallCalendarSpread(S, X1, X2, Time1, Time2, r, r_d, sigma1, sigma2) {
  return DiagonalSpread('C', S, X1, X2, Time1, Time2, r, r_d, sigma1, sigma2);
}

function DiagonalPutCalendarSpread(S, X1, X2, Time1, Time2, r, r_d, sigma1, sigma2) {
  return DiagonalSpread('P', S, X1, X2, Time1, Time2, r, r_d, sigma1, sigma2);
}

/**
 * Search for the downside/upside break-even using Newton-Raphson
 * with the help of the option deltas.
 * Lower Break-Even: Determine S^* < X such that Net Debit = CS(S*, X, Time=0, Time2=(t2-t1), r, r_d, sigma1=0, sigma2)
 * Upper Break-Even: Determine S^* > X such that Net Debit = CS(S*, X, Time=0, Time2=(t2-t1), r, r_d, sigma1=0, sigma2)
 *
 * Suche die Nullstelle mit Newton-Raphson und der Ableitung Greeks.GBSDelta()
 * @param X strike price of back-month option
 * @param Time2 remaining time to maturity in years for back month call (e.g. = Time2-Time1)
 * @param r risk-free rate of return
 * @param r_d underlying dividend yield
 * @param sigma2 volatility of back-month option
 * @param lower boolean whether to search for lower or upper break even point
 * @param epsilon accuracy, e.g. 1e-2
 */
function CallCalendarSpreadBreakEvenNR(X, Time2, r, r_d, sigma2, NetDebit, lower, epsilon) {
  let Si = lower ? X * 0.95 : X * 1.05; // start value
  const cm = NetDebit;
  const Time1 = 0; // maturity of the front-month option
  const sigma1 = 0; // maturity of the front-month option
  let ci = CalendarSpread('C', Si, X, Time1, Time2, r, r_d, sigma1, sigma2);
  const b = r - r_d;

  // delta of calendar spread is delta of long call - delta of sold call
  // at maturity of sold call, its delta is either 0 or -1
  let deltai = (lower ? 0 : -1) + Greeks.GBSDelta('C', Si, X, Time2, r, b, sigma2);
  let minDiff = Math.abs(cm - ci);
  let k = 0;
  const maxIter = 10;

  while (k < maxIter && Math.abs(cm - ci) >= epsilon && Math.abs(cm - ci) <= minDiff && ci > 0) {
    // TODO: Aufpassen, dass man mit Si > X landet für lower = TRUE bzw. Si < X für lower=FALSE
    Si = Si - (ci - cm) / deltai;

    // Quick Hack
    if (lower && Si > X) {
      Si = X * 0.98;
    }

    if (!lower && Si < X) {
      Si = X * 1.02;
    }

    ci = CalendarSpread('C', Si, X, Time1, Time2, r, r_d, sigma1, sigma2);
    deltai = (lower ? 0 : -1) + Greeks.GBSDelta('C', Si, X, Time2, r, b, sigma2);
    minDiff = Math.abs(cm - ci);
    k++;
  }

  if (Math.abs(cm - ci) < epsilon) {
    return Si;
  } else {
    return null;
  }
}

/**
 * Want to find Downside BreakEven (DBE; lower break-even) and Upside BreakEven (UBE; upper break-even)
 * for Put Calendar Spreads (CS).
 * at maturity of front month put (e.g. time1=0, time2>0)
 *
 * For front month put at maturity (time1=0), search for price S* such as
 * S* equals the NetDebit.
 * NetDebit = CalendarSpread(type="Put", S=S*, X=X, Time1=0, Time2=Time2, r, r_d=0, sigma1=0, sigma2, ratio=1)
 *
 * We solve for S* using Newton-Raphson (NR) and Delta() as the first derivative
 *
 * @param X strike price of back-month option
 * @param T remaining time to maturity in years for back month put (e.g. = Time2-Time1)
 * @param r risk-free rate of return
 * @param r_d underlying dividend yield
 * @param sigma2 volatility of back month put (front month vola sigma1 is irrelevant for front month payoff)
 * @param isLower boolean whether to search for lower or upper break even point (DBE or UBE)
 * @param epsilon accuracy, e.g. 1e-2
 */
function PutCalendarSpreadBreakEvenNR(X, Time2, r, r_d, sigma2, NetDebit, isLower, epsilon) {
  let Si = isLower ? X * 0.95 : X * 1.05; // start value for S
  const cm = NetDebit;
  const Time1 = 0; // maturity of the front-month option
  const sigma1 = 0; // maturity of the front-month option

  let ci = PutCalendarSpread(Si, X, Time1, Time2, r, r_d, sigma1, sigma2);
  let b = r - r_d;
  // delta of calendar spread is delta of long put - delta of sold put
  // at maturity of sold put, its delta is either +1 (S < X) or 0 (S >= X)
  let deltai = (isLower ? +1 : 0) + Greeks.GBSDelta('P', Si, X, Time2, r, b, sigma2);
  let minDiff = Math.abs(cm - ci);
  let k = 0;
  const maxIter = 10;

  while (k < maxIter && Math.abs(cm - ci) >= epsilon && Math.abs(cm - ci) <= minDiff && ci > 0) {
    // TODO: Aufpassen, dass man mit Si > X landet fuer lower = TRUE bzw. Si < X fuer lower=FALSE
    Si = Si - (ci - cm) / deltai;
    // Quick Hack
    if (isLower && Si > X) {
      Si = X * 0.98;
    }

    if (!isLower && Si < X) {
      Si = X * 1.02;
    }

    ci = PutCalendarSpread(Si, X, Time1, Time2, r, r_d, 0, sigma2);
    deltai = (isLower ? +1 : 0) + Greeks.GBSDelta('P', Si, X, Time2, r, b, sigma2);
    minDiff = Math.abs(cm - ci);
    k++;
  }

  if (Math.abs(cm - ci) < epsilon) {
    return Si;
  } else {
    return null;
  }
}

/**
 * Want to find Downside BreakEven (DBE; lower break-even) and Upside BreakEven (UBE; upper break-even)
 * for Diagonal Calendar Spreads (CS).
 * at maturity of front month option (e.g. time1=0, time2>0)
 *
 * For front month option at maturity (time1=0), search for price S* such as
 * S* equals the NetDebit.
 * NetDebit = CalendarSpread(type="Put", S=S*, X=X, Time1=0, Time2=Time2, r, r_d=0, sigma1=0, sigma2, ratio=1)
 *
 * We solve for S* using Newton-Raphson (NR) and Delta() as the first derivative
 *
 * @param X strike price of back-month option
 * @param T remaining time to maturity in years for back month option (e.g. = Time2-Time1)
 * @param r risk-free rate of return
 * @param r_d underlying dividend yield
 * @param sigma2 volatility of back month put (front month vola sigma1 is irrelevant for front month payoff)
 * @param isLower boolean whether to search for lower or upper break even point (DBE or UBE)
 * @param epsilon accuracy, e.g. 1e-2
 */
function DiagonalCallCalendarSpreadBreakEvenNR(X1, X2, Time2, r, r_d, sigma2, NetDebit, isLower, epsilon) {
  const Time1 = 0; // at maturity of the front-month option
  const sigma1 = 0; // volatility of the front-month option at maturity is zero

  let Si = isLower ? X1 * 0.95 : X1 * 1.05; // start value for S
  let ci = DiagonalCallCalendarSpread(Si, X1, X2, Time1, Time2, r, r_d, sigma1, sigma2);
  const b = r - r_d;
  //log.trace( "Initialize Breakeven Search. UnderlyingPrice:" + format( Si) + " TheoPrice:" + format( ci) + " NetDebit: " + format( NetDebit));

  // delta of diagonal calendar spread is delta of long call - delta of sold call
  // at maturity of sold call, its delta is either 0 or -1
  let deltai = (isLower ? 0 : -1) + Greeks.GBSDelta('C', Si, X2, Time2, r, b, sigma2);
  let minDiff = Math.abs(NetDebit - ci);
  let k = 0;
  const maxIter = 10;

  //log.trace( "Start Loop. UnderlyingPrice:" + format( Si) + " TheoPrice:" + format( ci) + " NetDebit: " + format( NetDebit) + " Delta:" + format( deltai) + " Mindiff:" + format( minDiff));
  while (k < maxIter && Math.abs(NetDebit - ci) >= epsilon && Math.abs(NetDebit - ci) <= minDiff) {
    // TODO: Aufpassen, dass man mit Si > X landet fuer lower = TRUE bzw. Si < X fuer lower=FALSE
    Si = Si - (ci - NetDebit) / deltai;
    if (Si < 0) {
      // log.trace( "Break because Si < 0. Value:" + format( Si));
      break;
    }
    // Quick Hack
    if (isLower && Si > X1) {
      Si = X1 * 0.98;
    }

    if (!isLower && Si < X1) {
      Si = X1 * 1.02;
    }

    ci = DiagonalCallCalendarSpread(Si, X1, X2, Time1, Time2, r, r_d, sigma1, sigma2);
    deltai = (isLower ? 0 : -1) + Greeks.GBSDelta('C', Si, X2, Time2, r, b, sigma2);
    minDiff = Math.abs(NetDebit - ci);

    //log.trace( "Iteration " + k + ":  UnderlyingPrice:" + format( Si) + " TheoPrice:" + format( ci) + " NetDebit: " + format( netDebit) + " Delta:" + format( deltai) + " Mindiff:"
    //		+ format( minDiff));
    //console.log("Iteration " + k + ":  UnderlyingPrice:" + Si + " TheoPrice:" + ci + " NetDebit: " + NetDebit + " Delta:" + deltai + " Mindiff:" + minDiff);
    k++;
  }

  if (Math.abs(NetDebit - ci) < epsilon) {
    return Si;
  } else {
    return null;
  }
}

/**
 * Want to find Downside BreakEven (DBE; lower break-even) and Upside BreakEven (UBE; upper break-even)
 * for Diagonal Put Calendar Spreads (CS).
 * at maturity of front month put (e.g. time1=0, time2>0)
 *
 * For front month put at maturity (time1=0), search for price S* such as
 * S* equals the NetDebit.
 * NetDebit = CalendarSpread(type="Put", S=S*, X=X, Time1=0, Time2=Time2, r, r_d=0, sigma1=0, sigma2, ratio=1)
 *
 * We solve for S* using Newton-Raphson (NR) and Delta() as the first derivative
 *
 * @param X strike price of back-month option
 * @param T remaining time to maturity in years for back month put (e.g. = Time2-Time1)
 * @param r
 * @param r_d
 * @param sigma2 volatility of back month put (front month vola sigma1 is irrelevant for front month payoff)
 * @param isLower boolean whether to search for lower or upper break even point (DBE or UBE)
 * @param epsilon accuracy, e.g. 1e-2
 */
function DiagonalPutCalendarSpreadBreakEvenNR(X1, X2, Time2, r, r_d, sigma2, NetDebit, isLower, epsilon) {
  const Time1 = 0; // at maturity of the front-month option
  const sigma1 = 0; // volatility of the front-month option at maturity is zero

  let Si = isLower ? X1 * 0.95 : X1 * 1.05; // start value for S
  let cm = NetDebit;

  let ci = DiagonalPutCalendarSpread(Si, X1, X2, Time1, Time2, r, r_d, sigma1, sigma2);
  const b = r - r_d;
  // delta of calendar spread is delta of long put - delta of sold put
  // at maturity of sold put, its delta is either +1 (S < X1) or 0 (S >= X1)
  let deltai = (isLower ? +1 : 0) + Greeks.GBSDelta('P', Si, X2, Time2, r, b, sigma2);
  let minDiff = Math.abs(cm - ci);
  let k = 0;
  const maxIter = 10;

  while (k < maxIter && Math.abs(cm - ci) >= epsilon && Math.abs(cm - ci) <= minDiff && ci > 0) {
    // TODO: Aufpassen, dass man mit Si > X landet fuer lower = TRUE bzw. Si < X fuer lower=FALSE
    Si = Si - (ci - cm) / deltai;
    // Quick Hack
    if (isLower && Si > X1) {
      Si = X1 * 0.98;
    }

    if (!isLower && Si < X1) {
      Si = X1 * 1.02;
    }

    ci = DiagonalPutCalendarSpread(Si, X1, X2, Time1, Time2, r, r_d, sigma1, sigma2);
    deltai = (isLower ? +1 : 0) + Greeks.GBSDelta('P', Si, X2, Time2, r, b, sigma2);
    minDiff = Math.abs(cm - ci);
    k++;
  }
  if (Math.abs(cm - ci) < epsilon) {
    return Si;
  } else {
    return null;
  }
}

function calendarSpreadBreakEven(soldLeg, boughtLeg, netDebit, riskFreeRate, dividendYield, lower) {
  let nearExpOption; // front-month option
  let farExpOption; // back-month option

  const maturity1 = new Date(Date.parse(boughtLeg.option['xopt._MATURITY_ISO'])).getTime();
  const maturity2 = new Date(Date.parse(soldLeg.option['xopt._MATURITY_ISO'])).getTime();
  //let frontMonthExpiryDate;
  //let backMonthExpiryDate;
  let sigma2; // back-month option implied volatility

  if (maturity1 < maturity2) {
    nearExpOption = boughtLeg;
    farExpOption = soldLeg;
    //frontMonthExpiryDate = maturity1;
    //backMonthExpiryDate = maturity2;
    sigma2 = parseFloat(farExpOption.option['q._VOLA_ASK']) / 100;
  } else {
    nearExpOption = soldLeg;
    farExpOption = boughtLeg;
    //frontMonthExpiryDate = maturity2;
    //backMonthExpiryDate = maturity1;
    sigma2 = parseFloat(farExpOption.option['q._VOLA_ASK']) / 100;
  }

  const strike1 = parseFloat(nearExpOption.option['xopt._STRIKE']);
  const strike2 = parseFloat(farExpOption.option['xopt._STRIKE']);
  const Time =
    getTimeToMaturityInYears(farExpOption.option['xopt._MATURITY_ISO']) -
    getTimeToMaturityInYears(nearExpOption.option['xopt._MATURITY_ISO']);
  let breakEven = null;

  if (farExpOption.option['xopt._TYPE_CP'] === 'C') {
    // Call Calendar Spread or Call Diagonal Spread
    if (strike1 === strike2) {
      breakEven = CallCalendarSpreadBreakEvenNR(
        strike1,
        Time,
        riskFreeRate,
        dividendYield,
        sigma2,
        netDebit,
        lower,
        1e-2
      ); // startExactlyAtStrike=false);
    } else {
      breakEven = DiagonalCallCalendarSpreadBreakEvenNR(
        strike1,
        strike2,
        Time,
        riskFreeRate,
        dividendYield,
        sigma2,
        netDebit,
        lower,
        1e-2
      ); // startExactlyAtStrike=false);
    }
  } else {
    // Put Spreads
    if (strike1 === strike2) {
      breakEven = PutCalendarSpreadBreakEvenNR(
        strike1,
        Time,
        riskFreeRate,
        dividendYield,
        sigma2,
        netDebit,
        lower,
        1e-2
      ); // startExactlyAtStrike=false);
    } else {
      breakEven = DiagonalPutCalendarSpreadBreakEvenNR(
        strike1,
        strike2,
        Time,
        riskFreeRate,
        dividendYield,
        sigma2,
        netDebit,
        lower,
        1e-2
      ); // startExactlyAtStrike=false);
    }
  }

  return breakEven;
}

function shortCalendarSpreadBreakEven(option1, option2, netDebit, riskFreeRate, dividendYield, lower) {
  let nearExpOption; // front-month option
  let farExpOption; // back-month option

  const maturity1 = new Date(Date.parse(option1.option['xopt._MATURITY_ISO'])).getTime();
  const maturity2 = new Date(Date.parse(option2.option['xopt._MATURITY_ISO'])).getTime();
  //let frontMonthExpiryDate;
  //let backMonthExpiryDate;
  let sigma2; // back-month option implied volatility

  if (maturity1 < maturity2) {
    //swap if necessary
    nearExpOption = option1;
    farExpOption = option2;
    //frontMonthExpiryDate = maturity1;
    //backMonthExpiryDate = maturity2;
    sigma2 = farExpOption.option['q._VOLA_ASK'] / 100;
  } else {
    nearExpOption = option2;
    farExpOption = option1;
    //frontMonthExpiryDate = maturity2;
    //backMonthExpiryDate = maturity1;
    sigma2 = farExpOption.option['q._VOLA_ASK'] / 100;
  }

  const strike1 = parseFloat(nearExpOption.option['xopt._STRIKE']);
  const strike2 = parseFloat(farExpOption.option['xopt._STRIKE']);
  const Time =
    getTimeToMaturityInYears(farExpOption.option['xopt._MATURITY_ISO']) -
    getTimeToMaturityInYears(nearExpOption.option['xopt._MATURITY_ISO']);

  let breakEven = null;

  if (nearExpOption.option['xopt._TYPE_CP'] === 'C') {
    if (strike1 === strike2) {
      breakEven = ShortCallCalendarSpreadBreakEvenNR(
        strike1,
        Time,
        riskFreeRate,
        dividendYield,
        sigma2,
        netDebit,
        lower,
        1e-2,
        false
      );
      /*
			if( Double.isNaN( breakEven)) {
				breakEven =  ShortCallCalendarSpread.ShortCalendarSpreadBreakEvenNR( strike, Time, riskFreeRate, dividendYield, sigma2, netDebit, lower, 1e-2, true);
			}*/
    }
  } else {
    if (strike1 === strike2) {
      breakEven = ShortPutCalendarSpreadBreakEvenNR(
        strike1,
        Time,
        riskFreeRate,
        dividendYield,
        sigma2,
        netDebit,
        lower,
        1e-2,
        false
      );
      /*
			if( Double.isNaN( breakEven)) {
				breakEven =  ShortPutCalendarSpread.ShortPutCalendarSpreadBreakEvenNR( strike, Time, riskFreeRate, dividendYield, sigma2, netDebit, lower, 1e-2, true);
			}*/
    }
  }

  return breakEven;
}

/**
 * Want to find Downside BreakEven (DBE; lower break-even) and Upside
 * BreakEven (UBE; upper break-even) for Call Calendar Spreads (CS). at
 * maturity of front month call (e.g. time1=0, time2>0)
 *
 * For front month call at maturity (time1=0), search for price S* such as
 * S* equals the NetDebit. NetDebit = CalendarSpread(type="Call", S=S*, X=X,
 * Time1=0, Time2=Time2, r, r_d=0, sigma1=0, sigma2, ratio=1)
 *
 * We solve for S* using Newton-Raphson (NR) and Delta() as the first
 * derivative
 *
 * @param X
 * @param Time2
 *            remaining time to maturity in years for back month call (e.g.
 *            = Time2-Time1)
 * @param r
 * @param r_d
 * @param sigma2
 *            volatility of back month call (front month vola sigma1 is
 *            irrelevant for front month payoff)
 * @param isLower
 *            boolean whether to search for lower or upper break even point
 *            (DBE or UBE)
 * @param epsilon
 *            accuracy, e.g. 1e-2
 */
function ShortCallCalendarSpreadBreakEvenNR(X, Time2, r, r_d, sigma2, NetDebit, isLower, epsilon, startAtExactStrike) {
  const Time1 = 0; // at maturity of the front-month option
  const sigma1 = 0; // volatility of the front-month option at maturity is zero

  let Si;
  if (startAtExactStrike) {
    Si = X;
  } else {
    Si = isLower ? X * 0.95 : X * 1.05; // start value for S
  }

  const cm = NetDebit;
  let ci = ShortCallCalendarSpread(Si, X, Time1, Time2, r, r_d, sigma1, sigma2);
  const b = r - r_d;

  // delta of short call calendar spread is delta of long front-month call - delta of sold back-month call
  // at maturity of long front-month call, its delta is either 0 or 1
  let deltai = (isLower ? 0 : 1) - Greeks.GBSDelta('C', Si, X, Time2, r, b, sigma2);
  let minDiff = Math.abs(cm - ci);
  let k = 0;
  const maxIter = 10;

  while (k < maxIter && Math.abs(cm - ci) >= epsilon && Math.abs(cm - ci) <= minDiff) {
    // TODO: Aufpassen, dass man mit Si > X landet fuer lower = TRUE bzw. Si < X fuer lower=FALSE
    Si = Si - (ci - cm) / deltai;
    // Quick Hack
    if (isLower && Si > X) {
      Si = X * 0.98;
    }
    if (!isLower && Si < X) {
      Si = X * 1.02;
    }

    ci = ShortCallCalendarSpread(Si, X, Time1, Time2, r, r_d, sigma1, sigma2);
    deltai = (isLower ? 0 : 1) - Greeks.GBSDelta('C', Si, X, Time2, r, b, sigma2);
    minDiff = Math.abs(cm - ci);
    k++;
  }

  if (Math.abs(cm - ci) < epsilon) {
    return Si;
  } else {
    return null;
  }
}

/**
 * Want to find Downside BreakEven (DBE; lower break-even) and Upside
 * BreakEven (UBE; upper break-even) for Put Calendar Spreads (PS). at
 * maturity of front month put (e.g. time1=0, time2>0)
 *
 * For front month put at maturity (time1=0), search for price S* such as
 * S* equals the NetDebit. NetDebit = CalendarSpread(type="Put", S=S*, X=X,
 * Time1=0, Time2=Time2, r, r_d=0, sigma1=0, sigma2, ratio=1)
 *
 * We solve for S* using Newton-Raphson (NR) and Delta() as the first
 * derivative
 *
 * @param X
 * @param Time2
 *            remaining time to maturity in years for back month put (e.g.
 *            = Time2-Time1)
 * @param r
 * @param r_d
 * @param sigma2
 *            volatility of back month option (front month vola sigma1 is
 *            irrelevant for front month payoff)
 * @param isLower
 *            boolean whether to search for lower or upper break even point
 *            (DBE or UBE)
 * @param epsilon
 *            accuracy, e.g. 1e-2
 */
function ShortPutCalendarSpreadBreakEvenNR(X, Time2, r, r_d, sigma2, NetDebit, isLower, epsilon, startAtExactStrike) {
  const Time1 = 0; // at maturity of the front-month option
  const sigma1 = 0; // volatility of the front-month option at maturity is zero

  let Si;
  if (startAtExactStrike) {
    Si = X;
  } else {
    Si = isLower ? X * 0.95 : X * 1.05; // start value for S
  }

  const cm = NetDebit;
  let ci = ShortPutCalendarSpread(Si, X, Time1, Time2, r, r_d, sigma1, sigma2);
  const b = r - r_d;

  // delta of short put calendar spread is delta of long front-month put - delta of sold back-month put
  // at maturity of long front-month put, its delta is either -1 or 0
  let deltai = (isLower ? -1 : 0) - Greeks.GBSDelta('P', Si, X, Time2, r, b, sigma2);
  let minDiff = Math.abs(cm - ci);
  let k = 0;
  const maxIter = 10;

  while (k < maxIter && Math.abs(cm - ci) >= epsilon && Math.abs(cm - ci) <= minDiff) {
    // TODO: Aufpassen, dass man mit Si > X landet fuer lower = TRUE bzw. Si < X fuer lower=FALSE
    Si = Si - (ci - cm) / deltai;
    // Quick Hack
    if (isLower && Si > X) Si = X * 0.98;
    if (!isLower && Si < X) Si = X * 1.02;

    ci = ShortPutCalendarSpread(Si, X, Time1, Time2, r, r_d, sigma1, sigma2);
    deltai = (isLower ? -1 : 0) - Greeks.GBSDelta('P', Si, X, Time2, r, b, sigma2);
    minDiff = Math.abs(cm - ci);
    k++;
  }

  if (Math.abs(cm - ci) < epsilon) {
    return Si;
  } else {
    return null;
  }
}

// not sure what this function does?
// solve for largest S*, where f(S*) is numerically 0?
function calcMaxUnderlyingPriceforZeroValue(leg1, leg2, dividendYield, riskFreeRate /*,vola*/) {
  const soldLeg = isSell(leg1) ? leg1 : leg2;

  const TypeFlag = soldLeg.option['xopt._TYPE_CP'];
  const X = parseFloat(soldLeg.option['xopt._STRIKE']);
  let Si = X * 0.98;
  const Time = getTimeToMaturityInYears(soldLeg.option['xopt._MATURITY_ISO']);
  const r = riskFreeRate;
  const r_d = dividendYield;
  //const b = r - r_d;
  const sigma = parseFloat(soldLeg.option['q._VOLA_ASK']) / 100;
  const accuracy = 0.1 / (soldLeg.quantity * soldLeg.option['xopt.LOT_SIZE_A']);

  let tempTheoPrice = Greeks.GBSOption(TypeFlag, Si, X, Time, r, r - r_d, sigma);
  let delta = Greeks.GBSDelta(TypeFlag, Si, X, Time, r, r - r_d, sigma);

  for (let i = 0; i < 10; i++) {
    Si = Si - tempTheoPrice / delta;
    tempTheoPrice = Greeks.GBSOption(TypeFlag, Si, X, Time, r, r - r_d, sigma);
    //console.log("Step= " + i + " S="+Si+" price="+ tempTheoPrice);

    if (tempTheoPrice <= accuracy) {
      //console.log("Steps needed = " + i);
      return Si;
    } else if (tempTheoPrice < 0) {
      return null;
    }
    delta = Greeks.GBSDelta(TypeFlag, Si, X, Time, r, r - r_d, sigma);
  }

  return null;
}
